import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import { CROWD_WORKER_SOURCE } from '@scale/shared/types/workerSource';
import { useState, useEffect } from 'react';

export const NOBODY_WORKER_SOURCE = 'nobody';

export interface WorkerSource {
  name: string;
  source: string;
}

export interface WorkerSourceGroup {
  name: string;
  fields: WorkerSource[];
}

interface FetchWorkerSourceGroupsOptions {
  includeNobody?: boolean;
}

interface FetchWorkerSourceOptionsOptions {
  filterOutCrowd?: boolean;
}

interface WorkerSourceResponse {
  // Key is the group name, value is an array of worker sources in that group
  [key: string]: Array<{ source: string; displayName: string }>;
}

export async function fetchWorkerSourceGroups(
  options: FetchWorkerSourceGroupsOptions = {},
): Promise<WorkerSourceGroup[]> {
  try {
    const response = await apiFetch<WorkerSourceResponse>(
      null,
      'corp-api/worker-sources/groups/list',
      {
        method: 'GET',
      },
    );

    // Convert the response to the format needed by the form
    const formattedGroups: WorkerSourceGroup[] = Object.entries(response)
      .filter(([_, sources]) => sources.length > 0) // Only include groups that have sources
      .map(([group, sources]) => ({
        name: group.charAt(0).toUpperCase() + group.slice(1).toLowerCase(), // Capitalize first letter
        fields: sources.map(source => ({
          name: source.displayName,
          source: source.source,
        })),
      }));

    // Add the "Nobody" group at the beginning if requested
    if (options.includeNobody) {
      formattedGroups.unshift({
        name: 'Nobody',
        fields: [{ name: 'Nobody', source: NOBODY_WORKER_SOURCE }],
      });
    }

    return formattedGroups;
  } catch (err) {
    defaultApiFetchErrorHandler(err, { handleHttp: true });
    throw err;
  }
}

export async function fetchWorkerSourceOptions(
  options: FetchWorkerSourceOptionsOptions = {},
): Promise<WorkerSource[]> {
  try {
    const response = await apiFetch<WorkerSourceResponse>(
      null,
      'corp-api/worker-sources/groups/list',
      {
        method: 'GET',
      },
    );

    // Flatten all worker sources from all groups
    const allWorkerSources = Object.values(response).flatMap(sources =>
      sources.map(source => ({
        name: source.displayName,
        source: source.source,
      })),
    );

    // Filter out CROWD_WORKER_SOURCE if requested
    const filteredWorkerSources = options.filterOutCrowd
      ? allWorkerSources.filter(source => source.source !== CROWD_WORKER_SOURCE)
      : allWorkerSources;

    const sourcesWithNoWorkerSource = options.filterOutCrowd
      ? [{ name: 'No Worker Source (crowd)', source: '' }, ...filteredWorkerSources]
      : filteredWorkerSources;

    return sourcesWithNoWorkerSource;
  } catch (err) {
    defaultApiFetchErrorHandler(err, { handleHttp: true });
    throw err;
  }
}

export function useWorkerSourceOptions(options: FetchWorkerSourceOptionsOptions = {}) {
  const [workerSourceOptions, setWorkerSourceOptions] = useState<WorkerSource[]>([]);
  const { filterOutCrowd } = options;

  useEffect(() => {
    const fetchSources = async () => {
      const sources = await fetchWorkerSourceOptions({ filterOutCrowd });
      setWorkerSourceOptions(sources);
    };

    fetchSources();
  }, [filterOutCrowd]);

  return workerSourceOptions;
}
